import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate } from "@/shared/utils/formatters";
import { StripeFinanceTotalsSummary } from "../components/StripeFinanceTotalsSummary";
import { StripeTransactionList } from "../components/StripeTransactionList";
import { useStripeFinances } from "../hooks/stripeQueries";
import { isStripeFinanceAvailable } from "../utils/stripeMoney";

function StripeFinances({ businessId, userId }: { businessId: string; userId: string }) {
  const finances = useStripeFinances(businessId, userId);
  const { goToStripe } = useAppNavigation();

  if (finances.isLoading) return <LoadingSpinner message="Loading finances..." />;
  if (finances.error || !finances.data) {
    return <ErrorState message={finances.error?.message ?? "Finances are unavailable."} onRetry={() => void finances.refetch()} />;
  }
  const summary = finances.data;
  if (!isStripeFinanceAvailable(summary.status)) {
    return (
      <EmptyState
        title="Connect Stripe"
        message="Connect your Stripe account to see your income and payments here."
        action={{ label: "Set up Stripe", onClick: goToStripe }}
      />
    );
  }
  const { start, end, timezone } = summary.period;
  const period = `${formatBusinessDate(start, timezone, { month: "short", day: "numeric" })} – ${formatBusinessDate(end, timezone, { month: "short", day: "numeric" })}`;
  const multiple = summary.totals.length > 1;

  return (
    <div className="space-y-6">
      {summary.totals.length === 0 ? (
        <Section title="This month" count={period} card>
          <EmptyState message="There are no Stripe payments this month yet." />
        </Section>
      ) : summary.totals.map((totals) => (
        <Section key={totals.currency} title={multiple ? `This month · ${totals.currency}` : "This month"} count={period} card>
          <StripeFinanceTotalsSummary totals={totals} summary={summary} showDeductions />
        </Section>
      ))}
      <StripeTransactionList businessId={businessId} userId={userId} />
      <p className="text-xs leading-relaxed text-muted-foreground">
        From your Stripe account{summary.environment === "test" ? " (test mode)" : ""}, as of{" "}
        {formatBusinessDate(summary.asOf, timezone, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.
        Gross income is what customers paid. Net income is after Stripe fees, refunds and disputes.
        New refunds and disputes can take a minute to appear in their filters.
      </p>
    </div>
  );
}

export function StripeFinancesPage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();
  return (
    <Page>
      <PageHeader title="Finances" />
      <PageContent>
        {loading ? (
          <LoadingSpinner message="Loading business..." />
        ) : business && businessId && user && business.ownerId === user.id ? (
          <StripeFinances key={`${businessId}:${user.id}`} businessId={businessId} userId={user.id} />
        ) : (
          <ErrorState message={business ? "Only the business owner can view finances." : "Select a business to view finances."} />
        )}
      </PageContent>
    </Page>
  );
}
